/* NRU shared photo swap - v3.0.
   One implementation of "tap the photo to see the next picture" for every
   product card on the store: theme cards (search results, related products,
   anywhere snippets/card-product.liquid is used), the collection page grid, the
   cart line items and the drawer panel rows. Timings, easing and rules are
   copied from sections/nru-featured-row.liquid, which keeps its own inline copy
   for the home page rows - those are skipped here so the two never both bind.

   v2 animates the two photos in place instead of building a flex track. The
   track version was squeezed to half width on Dawn cards, because base.css has

     .media > *:not(.zoom):not(.deferred-media__poster-button) {
       display: block; position: absolute; width: 100%; height: 100%;
     }

   at three classes of specificity, which beat the track's own display:flex and
   width:200%. Nothing is sized, moved or restructured now: each photo keeps the
   box, size and object-fit the theme gave it and only its transform changes, so
   the page is pixel-identical until a swap actually happens.

   v3 makes the rule the same everywhere: a product photo never opens the
   product page. If there is a second picture the photo slides across to it, and
   if there is not the photo simply does nothing - no cursor, no button role, no
   tab stop, nothing to press. The title link is what opens the product, and it
   is left exactly as the theme wrote it.

   Two things that are easy to get wrong and are handled on purpose:

   - .card__media is also Dawn's box for collection tiles and blog article
     cards. Those must keep opening their page from the picture, so a media box
     is only claimed once it has proved it belongs to a product.

   - The cart lines and the drawer panels are built from a product handle and
     render one photo with no way to reach the rest. Those rows ask the product
     JSON for a second picture while the browser is idle, so it is fetched and
     decoded long before a finger lands on the thumbnail.

   The photo NEVER navigates. On theme cards an invisible full-card link
   (.card__heading a::after) covers the image, so a tap over the photo is
   delivered to that link; this script claims those clicks in the capture phase,
   and only when the pointer really was inside the photo. */
(function () {
  'use strict';

  if (window.__nruPhotoSwap) return;
  window.__nruPhotoSwap = true;

  /* Every media box on the store that should slide. */
  var MEDIA_SELECTOR =
    '.card__media, [data-image-toggle], .nru-panel-media,' +
    ' [class*="nru-ec-item-image-wrapper-"]';

  /* The home page rows ship their own identical implementation. */
  var SKIP_SELECTOR = '.nru-row__media';

  /* Real controls layered over a card keep their own click. The wishlist heart
     stops propagation itself, but it does so while bubbling, which cannot stop
     a capture listener - so it has to be excluded by hand. */
  var CONTROL_SELECTOR =
    '.nru-wish-btn, button, input, select, textarea, label, summary,' +
    ' modal-opener, quick-add-bulk, [data-add-to-cart], [data-choose-options]';

  /* The links that carry the theme card's full-card overlay. */
  var CARD_LINK_SELECTOR = '.card__heading a, a.full-unstyled-link';
  var CARD_SELECTOR = '.card-wrapper, .card';

  /* Proof that a .card__media belongs to a product rather than to a collection
     tile or a blog article card. */
  var PRODUCT_CARD_SELECTOR = '.product-card-wrapper, [data-product-card]';

  /* Rows rendered from a product handle: the cart lines and the drawer panels.
     They are the only boxes that ask the server for a second photo. */
  var PANEL_SELECTOR = '.nru-panel-media, [class*="nru-ec-item-image-wrapper-"]';

  var SEEN = 'data-nru-ps'; /* scanned once; carries no styling */

  var STORE_KEY = 'nru-photo-swap-alt';
  var STORE_LIMIT = 150;
  var MAX_IN_FLIGHT = 3;

  var pending = {}; /* product path -> promise, so a handle is asked for once */
  var remembered = readStore();
  var rows = [];  /* every panel row being worked on, queued or in flight */
  var queue = []; /* the ones still waiting their turn */
  var inFlight = 0;
  var draining = false;
  var drainScheduled = false;

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  function matches(el, sel) {
    return !!(el && el.matches && el.matches(sel));
  }

  /* The second photo of a product a shopper has already met, kept for the rest
     of the visit so the cart, the saved items and the recently viewed rows
     never ask for the same one twice. Private mode throws on every call here,
     hence the try/catch around all of it. */
  function readStore() {
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (e) {}
    return {};
  }

  function remember(path, urls) {
    remembered[path] = urls;
    try {
      var keys = Object.keys(remembered);
      while (keys.length > STORE_LIMIT) delete remembered[keys.shift()];
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(remembered));
    } catch (e) {}
  }

  function onIdle(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 1500 });
    else setTimeout(fn, 250);
  }

  /* The element that holds the photos. Theme cards keep them one level below
     .card__media, in the .media box that already clips its overflow. */
  function mediaBox(el) {
    if (el.classList.contains('card__media')) return el.querySelector('.media') || el;
    return el;
  }

  function directImages(box) {
    var out = [];
    var kids = box.children;
    for (var i = 0; i < kids.length; i++) {
      if (kids[i].tagName === 'IMG') out.push(kids[i]);
      else if (kids[i].tagName === 'PICTURE') {
        var inner = kids[i].querySelector('img');
        if (inner) out.push(inner);
      }
    }
    return out;
  }

  /* Two <img> tags can point at the same picture in two sizes, which is not a
     second photo. Compare the file the URL ends at, with the query string, the
     scheme and any size suffix taken off - the product JSON and the theme do
     not always spell the same photo the same way. */
  function photoId(url) {
    var s = String(url || '').split('?')[0].split('#')[0];
    s = s.replace(/^https?:/i, '');
    return s.replace(/_(?:\d+x\d*|x\d+)(?=\.[a-z0-9]+$)/i, '').toLowerCase();
  }

  function isRendered(img) {
    if (img.hasAttribute('hidden')) return false;
    var s = window.getComputedStyle(img);
    return s.display !== 'none' && s.visibility !== 'hidden';
  }

  function imageSource(img) {
    return img.getAttribute('src') || img.currentSrc || '';
  }

  /* The first photo in the box that is really a different picture from the one
     on show, and is really being rendered. */
  function secondPhoto(imgs) {
    var first = photoId(imageSource(imgs[0]));
    for (var i = 1; i < imgs.length; i++) {
      if (!isRendered(imgs[i])) continue;
      if (photoId(imageSource(imgs[i])) === first) continue;
      return imgs[i];
    }
    return null;
  }

  function productPath(href) {
    var m = String(href || '').match(/^([^?#]*\/products\/[^\/?#]+)/);
    return m ? m[1] : '';
  }

  /* The product this card is for, read from the card's own data attribute when
     it has one, otherwise from the nearest enclosing element that links to
     exactly one product. If an ancestor links to several products we have
     climbed out of the card, so nothing is returned. */
  function cardInfo(el, box) {
    var info = { path: '', title: '' };
    info.path = productPath(el.getAttribute('data-product-url') || box.getAttribute('data-product-url'));

    var node = box.parentElement;
    var hops = 0;
    while (node && hops < 5) {
      var links = node.querySelectorAll('a[href*="/products/"]');
      if (links.length) {
        var first = productPath(links[0].getAttribute('href'));
        for (var i = 1; i < links.length; i++) {
          if (productPath(links[i].getAttribute('href')) !== first) {
            first = '';
            break;
          }
        }
        if (first) {
          if (!info.path) info.path = first;
          info.title = (links[0].textContent || '').replace(/\s+/g, ' ').trim();
        }
        break;
      }
      node = node.parentElement;
      hops++;
    }
    return info;
  }

  function decoded(img) {
    if (!img.decode) return Promise.resolve(img.naturalWidth > 0);
    return img.decode().then(
      function () { return img.naturalWidth > 0; },
      function () { return img.naturalWidth > 0; }
    );
  }

  /* Resolves true only for a photo that has really arrived and been decoded, so
     the first frame of a swap is never a blank box. A lazy photo below the fold
     simply resolves later, when the browser gets to it. */
  function whenLoaded(img) {
    return new Promise(function (resolve) {
      if (img.complete) {
        resolve(img.naturalWidth ? decoded(img) : false);
        return;
      }
      img.addEventListener('load', function () { resolve(decoded(img)); }, { once: true });
      img.addEventListener('error', function () { resolve(false); }, { once: true });
    });
  }

  /* A photo that belongs to a product but has nothing to show. It is not a
     link, not a button and not a tab stop; the click handler swallows taps on
     it and the stylesheet takes the pointer cursor back off. */
  function markInert(box) {
    box.classList.remove('nru-ps--dual');
    box.classList.add('nru-ps--inert');
  }

  /* Called once we know no second photo is coming. On a theme card this also
     pulls Dawn's full-card link in to the text, so the picture stops being part
     of the link and stops looking like one. */
  function settleInert(box) {
    if (!box.classList.contains('nru-ps--inert')) return;
    var wrapper = closest(box, '.card-wrapper');
    if (wrapper) wrapper.classList.add('nru-ps-inert-card');
  }

  /* Hands this media box to the shared behaviour. Called only once the second
     photo has loaded, so up to this point the card is untouched. */
  function enhance(box, main, alt, info) {
    /* Park the second photo one full width to the right before anything can
       reveal it, so no frame ever shows it on top of the first. */
    alt.classList.add('nru-ps__img');
    alt.classList.add('nru-ps__img--alt');
    alt.classList.remove('hidden');
    main.classList.add('nru-ps__img');
    main.classList.add('nru-ps__img--main');

    box.classList.add('nru-ps');
    /* Dawn crossfades and scales the same two photos on hover. Dropping the
       class leaves one hover behaviour - this one. Every rule it carries is
       opacity/transform/transition, so nothing about layout changes. */
    box.classList.remove('media--hover-effect');
    if (window.getComputedStyle(box).overflow === 'visible') box.style.overflow = 'hidden';

    /* Commit the resting transforms before transitions are armed, so the photo
       does not slide into place on first paint. */
    void box.offsetWidth;

    box.classList.remove('nru-ps--inert');
    var wrapper = closest(box, '.card-wrapper');
    if (wrapper) wrapper.classList.remove('nru-ps-inert-card');

    box.classList.add('nru-ps--ready');
    box.classList.add('nru-ps--dual');
    box.setAttribute('role', 'button');
    box.setAttribute('tabindex', '0');
    box.setAttribute(
      'aria-label',
      info && info.title ? 'Show the next photo of ' + info.title : 'Show the next photo'
    );
  }

  /* Up to the first two photo URLs of a product, or an empty list when it only
     has the one. Asked for once per handle per tab, and kept for the visit. */
  function loadPhotos(path) {
    if (Object.prototype.hasOwnProperty.call(remembered, path)) {
      return Promise.resolve(remembered[path] || []);
    }
    if (!pending[path]) {
      pending[path] = fetch(path + '.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('unavailable');
          return res.json();
        })
        .then(function (product) {
          var images = (product && product.images) || [];
          var urls = images.length > 1 ? [images[0], images[1]] : [];
          remember(path, urls);
          return urls;
        })
        .catch(function () {
          /* Nothing to show and nothing to say about it: the row stays inert. */
          return [];
        });
    }
    return pending[path];
  }

  /* The photo to slide in: the product's second one, unless the row is already
     showing that one, in which case the first. */
  function pickAlternate(urls, main) {
    if (!urls || urls.length < 2) return '';
    var shown = photoId(imageSource(main));
    for (var i = urls.length - 1; i >= 0; i--) {
      if (photoId(urls[i]) !== shown) return urls[i];
    }
    return '';
  }

  function sizedUrl(url, box, main) {
    var px = box.getBoundingClientRect().width;
    if (!px && main) px = main.getBoundingClientRect().width;
    if (!px) px = 300;
    var w = Math.round(px * (window.devicePixelRatio || 1));
    w = Math.min(1200, Math.max(200, Math.ceil(w / 100) * 100));
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* Builds the second photo for a row that was rendered without one. The new
     image is parked off to the right, absolutely positioned and inside a box
     that clips from the start, so appending it cannot show anything or move
     anything. A detached image never loads, so it has to be in the document to
     fetch - and it is removed again if it turns out to be a dead end. */
  function attachSecond(entry, url) {
    var box = entry.box;
    if (box.classList.contains('nru-ps--dual')) return null;

    var main = directImages(box)[0];
    if (!main) return null;

    var style = window.getComputedStyle(main);
    var alt = document.createElement('img');
    alt.className = 'nru-ps__img--injected nru-ps__img nru-ps__img--alt';
    alt.setAttribute('decoding', 'async');
    alt.setAttribute('aria-hidden', 'true');
    alt.alt = '';
    alt.style.objectFit = style.objectFit || 'cover';
    alt.style.objectPosition = style.objectPosition || '50% 50%';
    /* The box has to be both the containing block and the clip before the new
       image is in the document, not once the row is enhanced: the image is
       parked one full width to the right, so the first frame it paints in an
       unclipped box lands on the row's own title and buttons. */
    box.classList.add('nru-ps-clip');
    box.appendChild(alt);
    alt.src = sizedUrl(url, box, main);

    return whenLoaded(alt).then(function (ok) {
      if (!ok) {
        if (alt.parentNode) alt.parentNode.removeChild(alt);
        box.classList.remove('nru-ps-clip');
        settleInert(box);
        return;
      }
      enhance(box, main, alt, entry.info);
      if (entry.tapped) {
        /* Tapped before the photo was ready: finish the gesture now. */
        void box.offsetWidth;
        swap(box);
      }
    });
  }

  function run(entry) {
    var main = directImages(entry.box)[0];
    if (!main || !entry.box.isConnected) {
      release(entry);
      inFlight--;
      drain();
      return;
    }
    loadPhotos(entry.path)
      .then(function (urls) {
        var url = pickAlternate(urls, main);
        if (!url) {
          settleInert(entry.box);
          return null;
        }
        return attachSecond(entry, url);
      })
      .catch(function () {})
      .then(function () {
        release(entry);
        inFlight--;
        drain();
      });
  }

  function drain() {
    while (draining && inFlight < MAX_IN_FLIGHT && queue.length) {
      inFlight++;
      run(queue.shift());
    }
  }

  function startDraining() {
    dropDetached();
    draining = true;
    drain();
  }

  function unwatch(box) {
    if (!rowWatcher) return;
    try { rowWatcher.unobserve(box); } catch (e) {}
  }

  /* A row is let go the moment it is settled, whichever way it went: with a
     second photo, without one, or with a photo that never arrived. Letting go
     here rather than only when a row scrolls into view is what stops the
     observer collecting the thumbnails of every panel the shopper has opened. */
  function release(entry) {
    var at = rows.indexOf(entry);
    if (at > -1) rows.splice(at, 1);
    var waiting = queue.indexOf(entry);
    if (waiting > -1) queue.splice(waiting, 1);
    unwatch(entry.box);
  }

  /* Rows whose panel was rebuilt before their turn came round. Swept once per
     idle batch, over the handful of rows still outstanding. */
  function dropDetached() {
    for (var i = rows.length - 1; i >= 0; i--) {
      if (rows[i].box.isConnected) continue;
      release(rows[i]);
    }
  }

  /* Rows are collected as they are found and fetched while the browser has
     nothing better to do, a few at a time, so a panel full of thumbnails never
     turns into a burst of requests. */
  function queueRow(entry) {
    rows.push(entry);
    queue.push(entry);
    if (rowWatcher) {
      try { rowWatcher.observe(entry.box); } catch (e) {}
    }
    if (draining) {
      drain();
      return;
    }
    if (drainScheduled) return;
    drainScheduled = true;
    onIdle(startDraining);
  }

  /* A row the shopper is looking at, or has just tapped, goes to the front. A
     tap on a row whose photo is already on its way is remembered too, so the
     gesture is finished the moment the photo lands. */
  function promote(box, tapped) {
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].box !== box) continue;
      if (tapped) rows[i].tapped = true;
      var waiting = queue.indexOf(rows[i]);
      if (waiting > 0) queue.unshift(queue.splice(waiting, 1)[0]);
      startDraining();
      return;
    }
  }

  var rowWatcher = null;
  try {
    rowWatcher = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          rowWatcher.unobserve(entries[i].target);
          promote(entries[i].target, false);
        }
      },
      { rootMargin: '200px' }
    );
  } catch (e) {}

  function bind(el) {
    if (!el || el.nodeType !== 1) return;
    if (el.classList.contains('nru-row__media') || closest(el, SKIP_SELECTOR)) return;

    var box = mediaBox(el);
    if (!box || box.getAttribute(SEEN)) return;
    box.setAttribute(SEEN, '1');

    var info = cardInfo(el, box);
    var isPanel = matches(el, PANEL_SELECTOR);

    /* Collection tiles and blog article cards are built out of .card__media
       too. Their picture still opens their page, so leave them completely
       alone unless this box has proved it belongs to a product. */
    if (!info.path && !isPanel && !closest(el, PRODUCT_CARD_SELECTOR)) return;

    /* From here on this is a product photo, and a product photo never
       navigates - with or without a second picture behind it. */
    markInert(box);

    var imgs = directImages(box);
    var second = imgs.length > 1 ? secondPhoto(imgs) : null;

    if (second) {
      whenLoaded(second).then(function (ok) {
        if (ok) enhance(box, imgs[0], second, info);
        else settleInert(box);
      });
      return;
    }

    if (imgs.length && isPanel && info.path) {
      queueRow({ box: box, path: info.path, info: info, tapped: false });
      return;
    }

    settleInert(box);
  }

  function scan() {
    var nodes = document.querySelectorAll(MEDIA_SELECTOR);
    for (var i = 0; i < nodes.length; i++) bind(nodes[i]);
  }

  /* The same over one freshly inserted piece of the page, so the cost is the
     size of what just arrived rather than the size of the document. */
  function scanTree(node) {
    if (!node || node.nodeType !== 1) return;
    if (matches(node, MEDIA_SELECTOR)) bind(node);
    var nodes = node.querySelectorAll(MEDIA_SELECTOR);
    for (var i = 0; i < nodes.length; i++) bind(nodes[i]);
  }

  function swap(box) {
    box.classList.add('is-touched');
    box.classList.toggle('is-alt');
  }

  /* Where the gesture that produced the current click started. It is the
     fallback that keeps taps honest on the rare engine that forwards a touch
     click without coordinates, and it is only trusted for the gesture that is
     still in progress. */
  var lastPointer = { x: 0, y: 0, at: 0 };

  function notePointer(event) {
    var t = event.touches && event.touches[0] ? event.touches[0] : event;
    if (typeof t.clientX !== 'number') return;
    lastPointer.x = t.clientX;
    lastPointer.y = t.clientY;
    lastPointer.at = Date.now();
  }

  document.addEventListener('pointerdown', notePointer, { capture: true, passive: true });
  document.addEventListener('touchstart', notePointer, { capture: true, passive: true });

  /* A click the keyboard made by activating a link, rather than one a finger
     or a mouse made. It carries no pointer type, no click count and no screen
     position; a pointer click reports at least one of the three, even on the
     engine that forwards a tap with its client coordinates zeroed. */
  function fromKeyboard(event) {
    return !event.pointerType && !event.detail && !event.screenX && !event.screenY;
  }

  function insidePhoto(event, box) {
    var x = event.clientX;
    var y = event.clientY;
    if (!x && !y) {
      /* No coordinates. The keyboard never touched the photo, whatever else
         was tapped a moment ago; only a pointer click that lost them falls
         back to where the gesture started. */
      if (fromKeyboard(event)) return false;
      if (Date.now() - lastPointer.at > 700) return false;
      x = lastPointer.x;
      y = lastPointer.y;
      if (!x && !y) return false;
    }
    var r = box.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  var CLAIMED_SELECTOR = '.nru-ps--dual, .nru-ps--inert';

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target) return;
      if (target.nodeType !== 1) target = target.parentElement;
      if (!target) return;
      if (closest(target, CONTROL_SELECTOR)) return;
      if (closest(target, SKIP_SELECTOR)) return;

      var box = closest(target, CLAIMED_SELECTOR);
      if (!box) {
        /* The click may have landed on the full-card link that covers the
           photo. Claim it only when the pointer was inside the photo, so taps
           on the title and keyboard activations still open the product. */
        var link = closest(target, CARD_LINK_SELECTOR);
        if (!link) return;
        var card = closest(link, CARD_SELECTOR);
        if (!card) return;
        box = card.querySelector(CLAIMED_SELECTOR);
        if (!box || !insidePhoto(event, box)) return;
      }

      /* A product photo never navigates and never reaches anything layered
         behind it. */
      event.preventDefault();
      event.stopPropagation();

      if (box.classList.contains('nru-ps--dual')) swap(box);
      else promote(box, true);
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    var box = event.target;
    if (!box || !box.classList || !box.classList.contains('nru-ps--dual')) return;
    event.preventDefault();
    swap(box);
  });

  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', scan);

  /* Collection filtering, search re-render, pagination and the drawers all
     replace cards after the page has loaded. Whatever has just been added is
     picked up there and then, so a thumbnail is never on screen with nothing
     behind it and the shopper's first tap on it is never swallowed; the sweep
     of the whole page is the expensive half and stays on a debounce behind it,
     because panels re-render often. A box is only ever scanned once, so
     neither pass can bind the same card twice. */
  try {
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) scanTree(added[j]);
      }
      clearTimeout(window.__nruPhotoSwapTimer);
      window.__nruPhotoSwapTimer = setTimeout(scan, 300);
    });
    if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}
})();